import React, { FC, memo, useId, useState } from 'react'
import { Button } from 'primereact/button'
import { Editor, EditorTextChangeEvent } from 'primereact/editor'
import { Column } from 'primereact/column'

import useText from '../../../common/hooks/useText'
import {
    useLogGetInteractionLogItemsQuery,
    useLogAddInteractionLogItemMutation,
    useLogDeleteInteractionLogItemMutation,
    InteractionLogItem,
} from '../../../common/redux/api/web-api'
import { dateLocalize } from '../../../lib/date-localize'
import { notifyError, notifySuccess } from '../../../common/features/notificationsManager/Notify'
import { Panel } from '../../../common/uikit/Panel'
import { confirm } from '../../../common/uikit/ConfirmDialog'
import { Table } from '../../../common/uikit/Table'

type InteractionLogsWidgetProps =
    | { chargePointId: string; companyId?: never }
    | { companyId: string; chargePointId?: never }

export const InteractionLogsWidget: FC<InteractionLogsWidgetProps> = memo((props) => {
    const text = useText()
    const formId = useId()
    const [message, setMessage] = useState<string | null>(null)
    const [expandedMessages, setExpandedMessages] = useState<number[]>([])

    const queryArg = props.chargePointId ? { chargePointId: props.chargePointId } : { companyId: props.companyId }

    const {
        data: interactions,
        isLoading,
        isFetching,
        refetch,
    } = useLogGetInteractionLogItemsQuery(queryArg, { refetchOnMountOrArgChange: true })

    const [addInteraction, { isLoading: isAdding }] = useLogAddInteractionLogItemMutation()
    const [deleteInteraction] = useLogDeleteInteractionLogItemMutation()

    const handleAdd = async () => {
        if (!message) return

        try {
            await addInteraction({
                addInteractionLogItem: {
                    ...(props.chargePointId ? { chargePointId: props.chargePointId } : { companyId: props.companyId }),
                    message,
                },
            }).unwrap()
            setMessage(null)
            notifySuccess(text('msgInteractionLogAdded'))
            refetch()
        } catch {
            notifyError(text('msgCouldNotAddInteractionLog'))
        }
    }

    const handleDelete = async (logItemId: number) => {
        const confirmed = await confirm({
            header: text('lblProceedConfirmation'),
            message: text('msgDeleteInteractionLogConfirmation'),
            variant: 'danger',
        })
        if (!confirmed) return

        try {
            await deleteInteraction({ logItemId }).unwrap()
            notifySuccess(text('msgInteractionLogDeleted'))
            refetch()
        } catch {
            notifyError(text('msgCouldNotDeleteInteractionLog'))
        }
    }

    const interactionItems = interactions?.items ?? []

    const editorHeader = (
        <span className="ql-formats">
            <button className="ql-bold" aria-label="Bold"></button>
            <button className="ql-italic" aria-label="Italic"></button>
            <button className="ql-underline" aria-label="Underline"></button>
            <button className="ql-list" value="ordered" aria-label="Ordered List"></button>
            <button className="ql-list" value="bullet" aria-label="Unordered List"></button>
        </span>
    )

    const toggleExpanded = (itemId: number) => {
        setExpandedMessages((prev) => {
            if (prev.includes(itemId)) {
                return prev.filter((id) => id !== itemId)
            } else {
                return [...prev, itemId]
            }
        })
    }

    const messageBodyTemplate = (item: InteractionLogItem) => {
        const messageText = item.message ?? ''
        // Consider message "long" if it has more than 200 chars OR contains multiple block elements (p, ul, ol, div)
        const blockElementCount = (messageText.match(/<(p|ul|ol|div|h[1-6]|blockquote)/g) || []).length
        const isLongMessage = messageText.length > 200 || blockElementCount > 2
        const isExpanded = item.id ? expandedMessages.includes(item.id) : false

        return (
            <div data-testid={`interaction-log-${item.id}`}>
                <div
                    dangerouslySetInnerHTML={{ __html: messageText }}
                    style={{
                        maxHeight: isExpanded || !isLongMessage ? 'none' : '100px',
                        overflow: 'hidden',
                        position: 'relative',
                    }}
                />
                {isLongMessage && item.id && (
                    <Button
                        type="button"
                        label={isExpanded ? text('btnShowLess') : text('btnShowMore')}
                        link
                        size="small"
                        onClick={() => toggleExpanded(item.id!)}
                        className="p-0 mt-1"
                    />
                )}
            </div>
        )
    }

    const infoBodyTemplate = (item: InteractionLogItem) => (
        <div className="flex flex-column gap-1 text-500 text-sm">
            <span>{item.userEmail}</span>
            <span>{dateLocalize(item.createdAt)}</span>
        </div>
    )

    const actionsBodyTemplate = (item: InteractionLogItem) => (
        <div className="flex justify-content-end gap-2">
            <Button
                type="button"
                icon="pi pi-trash"
                severity="danger"
                size="small"
                outlined
                rounded
                onClick={() => item.id && handleDelete(item.id)}
                data-testid={`btn-delete-interaction-${item.id}`}
            />
        </div>
    )

    return (
        <Panel
            title={text('lblInteractionLogWidget')}
            headerIcon="pi pi-comments"
            data-testid="interaction-logs-widget"
            headerActions={
                <Button
                    type="button"
                    icon="pi pi-refresh"
                    onClick={() => refetch()}
                    outlined
                    size="small"
                    tooltip={text('btnRefresh')}
                    tooltipOptions={{ position: 'top' }}
                    loading={isFetching}
                    data-testid="btn-refresh-interactions"
                />
            }
            footerActions={
                <Button
                    loading={isAdding}
                    disabled={isAdding || !message}
                    label={text('btnAdd')}
                    data-testid="btn-save-interaction"
                    type="submit"
                    form={formId}
                    icon="pi pi-plus"
                />
            }
        >
            <form
                id={formId}
                className="mt-3"
                onSubmit={(event) => {
                    event.preventDefault()
                    handleAdd()
                }}
            >
                <Editor
                    value={message ?? ''}
                    onTextChange={(e: EditorTextChangeEvent) => setMessage(e.htmlValue)}
                    style={{ height: '150px' }}
                    headerTemplate={editorHeader}
                    data-testid="interaction-editor"
                />
            </form>

            <div className="mt-3">
                <Table
                    value={interactionItems}
                    loading={isLoading}
                    paginator
                    simplified
                    emptyMessage={text('lblNoInteractionLogsAvailable')}
                    data-testid="interaction-logs-table"
                    key={expandedMessages.join(',')}
                >
                    <Column
                        header={text('lblMessage')}
                        body={messageBodyTemplate}
                        style={{ width: '60%', verticalAlign: 'top' }}
                    />
                    <Column
                        header={text('lblInfo')}
                        body={infoBodyTemplate}
                        style={{ width: '30%', verticalAlign: 'top' }}
                    />
                    <Column body={actionsBodyTemplate} style={{ width: '10%', verticalAlign: 'top' }} />
                </Table>
            </div>
        </Panel>
    )
})
